import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRightSquare, Sparkles } from "lucide-react";

const TIERS = [
  {
    name: "Starter Partner",
    tier: "starter",
    price: "₱999",
    priceLabel: "Entry Kit",
    retail: "Up to 10% retail margin",
    accent: "border-white/15",
    features: [
      "2 physical products (retail value ₱1,598)",
      "Personal replicated online store",
      "Affiliate dashboard & links",
      "AI marketing assistant",
      "Digital onboarding & training",
      "Marketing materials library",
      "Referral link",
    ],
    cta: "Start Selling",
  },
  {
    name: "Business Builder",
    tier: "builder",
    price: "₱2,499",
    priceLabel: "Growth Kit",
    retail: "Up to 18% retail margin",
    accent: "border-[#FBBF24]/60 gold-border",
    badge: "Most Popular",
    features: [
      "5 physical products",
      "Higher member wholesale discount",
      "Faster fulfillment tier",
      "Bonus marketing asset pack",
      "Team override eligibility (L1-L3)",
      "Priority AI content generation",
      "Dedicated onboarding session",
    ],
    cta: "Build My Business",
  },
  {
    name: "Executive Partner",
    tier: "executive",
    price: "₱4,999",
    priceLabel: "Executive Kit",
    retail: "Up to 25% retail margin",
    accent: "border-[#FBBF24]",
    features: [
      "12 physical products",
      "Deepest wholesale pricing",
      "Priority support",
      "Exclusive product access",
      "Full team override tree (L1-L5)",
      "Monthly leadership incentives",
      "1:1 mentor pairing",
    ],
    cta: "Go Executive",
  },
];

export default function Membership() {
  return (
    <div className="space-y-10" data-testid="membership-page">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">
          <Sparkles size={12} /> Business Starter Kits
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight max-w-3xl">
          Pick the kit that fits your{" "}
          <span className="text-[#FBBF24]">ambition.</span>
        </h1>
        <p className="text-white/70 max-w-3xl mt-5 leading-relaxed">
          Every kit ships with real products, your personal online store, AI content tools, and full training —
          so you can start selling the day you activate.
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-3">
        {TIERS.map((t, i) => (
          <div key={t.name} className={`glass-card p-6 border ${t.accent} relative`} data-testid={`membership-tier-${i}`}>
            {t.badge && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest font-bold bg-[#FBBF24] text-[#0F2042] px-3 py-1 rounded-full">
                {t.badge}
              </span>
            )}
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 font-bold">{t.priceLabel}</div>
            <div className="font-display text-2xl font-black mt-1">{t.name}</div>
            <div className="font-mono-num text-4xl font-black text-[#FBBF24] mt-3">{t.price}</div>
            <div className="text-[#FBBF24] text-sm font-semibold mt-1">{t.retail}</div>
            <ul className="mt-5 space-y-2 text-sm text-white/75">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-[#FBBF24] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold uppercase tracking-widest bg-[#FBBF24] text-[#0F2042] hover:bg-yellow-300"
              data-testid={`membership-cta-${i}`}
            >
              {t.cta} <ArrowUpRightSquare size={14} />
            </Link>
          </div>
        ))}
      </div>

      <div className="glass-card p-6">
        <h2 className="font-display text-xl font-black mb-3">Every kit includes</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm text-white/75">
          {[
            "Physical product catalog",
            "Personal online store",
            "Affiliate dashboard",
            "Marketing materials library",
            "Training academy",
            "AI content assistant",
            "Wholesale pricing",
            "Order tracking",
            "Commission wallet",
            "Customer management",
          ].map((f) => (
            <div key={f} className="flex items-start gap-2">
              <CheckCircle2 size={14} className="text-[#FBBF24] mt-0.5 shrink-0" /> {f}
            </div>
          ))}
        </div>
      </div>

      <p className="text-center text-[11px] uppercase tracking-[0.22em] text-white/40">
        No lock-in · Cancel anytime · Earnings vary based on sales performance
      </p>
    </div>
  );
}
